/** Pixel ↔ physical unit conversions for the Tier 3 Group D Slice 1d
 *  Document Settings dialog. All conversions key off a DPI value:
 *  with DPI in dots-per-inch, physical width = pixels / DPI inches.
 *
 *  Single source of truth — the dialog renders three readouts
 *  (in / cm / mm) that derive from the same formulas, and the PDF
 *  export pipeline rounds points-per-page off the same numbers. */

const DEFAULT_DPI = 72;

/** A document with no declared DPI ("unspecified") behaves like 72.
 *  Exported for tests + the PDF pipeline so all paths agree. */
export function effectiveDpi(dpi: number | null | undefined): number {
  if (typeof dpi !== 'number' || !Number.isFinite(dpi) || dpi <= 0) return DEFAULT_DPI;
  return dpi;
}

export function pxToInches(px: number, dpi: number | null | undefined): number {
  return px / effectiveDpi(dpi);
}

export function pxToCm(px: number, dpi: number | null | undefined): number {
  return (px / effectiveDpi(dpi)) * 2.54;
}

export function pxToMm(px: number, dpi: number | null | undefined): number {
  return (px / effectiveDpi(dpi)) * 25.4;
}

/** Inverse helpers — for a future Image Size dialog that lets the
 *  user enter a physical size and snap pixel dimensions to match.
 *  Not used by slice 1d's metadata-only dialog, but ship together
 *  so the conversion table reads as one unit. */
export function inchesToPx(inches: number, dpi: number | null | undefined): number {
  return Math.round(inches * effectiveDpi(dpi));
}

export function cmToPx(cm: number, dpi: number | null | undefined): number {
  return Math.round((cm / 2.54) * effectiveDpi(dpi));
}

export function mmToPx(mm: number, dpi: number | null | undefined): number {
  return Math.round((mm / 25.4) * effectiveDpi(dpi));
}

/** Stock DPI presets shown as radio buttons in the dialog. */
export const DPI_PRESETS = [72, 150, 300] as const;
export type DpiPreset = (typeof DPI_PRESETS)[number];

/** PDF / print pipelines want their page sized in PostScript points
 *  (72 pt = 1 in). Given a pixel dimension and a document DPI, this
 *  returns the equivalent points so the rendered page lines up with
 *  the user's intended physical print size. */
export function pxToPoints(px: number, dpi: number | null | undefined): number {
  return (px / effectiveDpi(dpi)) * 72;
}
